<template>
  <v-app-bar app height="100" scroll-behavior="elevate" class="modern-navbar">
    <v-container class="container-fixed">
      <v-row align="center" justify="space-between" class="nav-wrapper" no-gutters>
        <!-- Logo -->
        <h2 class="navbar-logo">
          <router-link to="/">
            <img :src="require('@/assets/logo.png')" alt="MNK Agriventures Logo" />
          </router-link>
        </h2>

        <!-- Desktop Links -->
        <div class="nav-links d-none d-md-flex">
          <router-link
            v-for="(link, index) in links"
            :key="index"
            :to="link.route"
            class="nav-link"
            :class="{ selected: isActive(link.route) }"
          >
            {{ $t(link.name) }}
          </router-link>
        </div>

        <!-- Desktop Get in Touch Button -->
        <v-btn class="btn-design d-none d-md-flex" rounded elevation="0" to="/contact">
          {{ $t('Get in Touch') }}
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>

        <!-- Language Dropdown (Always Visible) -->
        <v-menu offset-y transition="scale-transition" class="lang-menu">
          <template v-slot:activator="{ props }">
            <v-btn v-bind="props" text class="lang-btn">
              <img :src="selectedFlag" class="flag-icon" alt="flag" />
              {{ selectedLangName }}
              <v-icon right>mdi-menu-down</v-icon>
            </v-btn>
          </template>

          <v-list class="lang-list">
            <v-list-item v-for="(lang, code) in languages" :key="code" @click="changeLang(code)">
              <img :src="lang.flag" class="flag-icon mr-2" />
              <v-list-item-title>{{ lang.name }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <!-- Mobile Hamburger -->
        <v-btn icon class="d-flex d-md-none" @click="drawer = true">
          <v-icon size="32">mdi-menu</v-icon>
        </v-btn>
      </v-row>
    </v-container>
  </v-app-bar>

  <!-- Mobile Drawer -->
  <v-navigation-drawer
    v-model="drawer"
    temporary
    right
    width="280"
    class="mobile-drawer"
    overlay-color="rgba(0,0,0,0.6)"
  >
    <!-- Drawer Header -->
    <div class="drawer-header d-flex justify-end pa-4">
      <v-btn icon @click="drawer = false" flat>
        <v-icon size="28">mdi-close</v-icon>
      </v-btn>
    </div>

    <!-- Drawer Links -->
    <v-list dense class="px-4">
      <v-list-item v-for="(link, index) in links" :key="index" @click="navigate(link.route)">
        <v-list-item-title :class="{ selected: isActive(link.route) }">
          {{ $t(link.name) }}
        </v-list-item-title>
      </v-list-item>

      <v-divider class="my-4"></v-divider>

      <!-- Get in Touch -->
      <v-btn color="green darken-2" block class="btn-design" rounded flat @click="navigate('/contact')">
        {{ $t('Get in Touch') }}
        <v-icon right>mdi-arrow-right</v-icon>
      </v-btn>
    </v-list>
  </v-navigation-drawer>
</template>

<script>
import { loadLocaleMessages } from "@/i18n";

export default {
  data() {
    return {
      drawer: false,
      locale: this.$i18n.locale,
      links: [
        { name: "Home", route: "/" },
        { name: "Who We Are", route: "/about-us" },
        { name: "What We Do", route: "/services" },
        { name: "Media & Updates", route: "/media" },
      ],
      languages: {
        en: { name: "ENG", flag: require("@/assets/flag/uk.png") },
        sw: { name: "SWA", flag: require("@/assets/flag/tz.png") },
        fr: { name: "FRE", flag: require("@/assets/flag/fr.png") },
        ar: { name: "AR", flag: require("@/assets/flag/ar.png") },
      },
    };
  },
  computed: {
    selectedLangName() {
      return this.languages[this.locale]?.name || "Language";
    },
    selectedFlag() {
      return this.languages[this.locale]?.flag || "";
    },
  },
  methods: {
    isActive(route) {
      return this.$route.path === route;
    },
    navigate(route) {
      this.$router.push(route);
      this.drawer = false;
    },
    async changeLang(lang) {
      this.locale = lang;
      this.$i18n.locale = lang;
      await loadLocaleMessages(lang);
    },
  },
};
</script>

<style scoped>
/* Navbar */
.modern-navbar {
  width: 100%;
  background-color: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(46, 125, 50, 0.2);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 999;
}
.container-fixed {
  max-width: 1200px; /* or whatever width you prefer */
  margin: 0 auto;
  padding: 0 16px; /* some side padding */
}


.nav-wrapper {
  justify-content: space-between;
  width: 100%;
}

.nav-content {
  display: flex;
  align-items: center;
  gap: 20px;
}

.navbar-logo img {
  height: 80px;
  width: auto;
  object-fit: contain;
  cursor: pointer;
}

/* Desktop Links */
.nav-links {
  display: flex;
  align-items: center;
  gap: 30px;
}

.nav-link {
  font-weight: 600;
  color: #2e7d32;
  text-decoration: none;
  text-transform: uppercase;
  position: relative;
  padding-bottom: 6px;
  transition: color 0.3s ease;
}

.nav-link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0%;
  height: 3px;
  background: linear-gradient(90deg, #06923e, #b87c4c);
  border-radius: 2px;
  transition: width 0.3s ease;
}

.nav-link:hover::after,
.nav-link.selected::after {
  width: 100%;
}

.nav-link:hover {
  color: #06923e;
}

.nav-link.selected {
  color: #b87c4c;
}

/* Language Button */
.flag-icon {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  object-fit: cover;
}

.lang-btn {
  font-weight: 600;
  color: #2e7d32;
  text-transform: none;
  background-color: rgba(255, 255, 255, 0.3);
  transition: 0.3s;
}

.lang-btn:hover {
  background-color: rgba(46, 125, 50, 0.15);
}

/* Mobile Drawer */
.mobile-drawer {
  background: #f9f9f9;
  border-left: 1px solid #ddd;
}

.drawer-header {
  border-bottom: 1px solid #ccc;
}

.mobile-drawer .v-list-item-title {
  font-weight: 600;
  font-size: 1.1rem;
  color: #2e7d32;
}

.mobile-drawer .v-list-item-title.selected {
  color: #b87c4c;
}

/* Responsive */
@media (max-width: 960px) {
  .nav-content {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
